import React, { useEffect, useState } from 'react';
import { CheckCircle, Package, Mail, ArrowRight, Loader } from 'lucide-react';
import { supabase } from '../lib/supabase';

const PurchaseSuccess: React.FC = () => {
  const searchParams = new URLSearchParams(window.location.search);
  const [loading, setLoading] = useState(true);
  const [purchaseData, setPurchaseData] = useState<any>(null);
  const sessionId = searchParams.get('session_id');

  useEffect(() => {
    if (sessionId) {
      fetchPurchaseDetails();
    } else {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId]);

  const fetchPurchaseDetails = async () => {
    try {
      // Fetch purchase details using session ID
      const { data } = await supabase
        .from('purchases')
        .select('*')
        .eq('stripe_checkout_session_id', sessionId)
        .single();

      if (data) {
        setPurchaseData(data);
      }
    } catch (err) {
      console.error('Error fetching purchase details:', err);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-green-50 to-green-100 flex items-center justify-center">
        <div className="text-center">
          <Loader className="w-12 h-12 text-green-600 animate-spin mx-auto mb-4" />
          <p className="text-gray-600">Loading your purchase details...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-green-50 to-green-100 flex items-center justify-center p-4">
      <div className="max-w-2xl w-full">
        {/* Success Card */}
        <div className="bg-white rounded-3xl shadow-2xl overflow-hidden">
          {/* Header */}
          <div className="bg-gradient-to-r from-green-500 to-green-600 p-8 text-center">
            <div className="inline-flex items-center justify-center w-20 h-20 bg-white rounded-full mb-4">
              <CheckCircle className="w-12 h-12 text-green-500" />
            </div>
            <h1 className="text-3xl font-bold text-white mb-2">
              Purchase Successful!
            </h1>
            <p className="text-green-100 text-lg">
              Thank you for choosing TraqPack
            </p>
          </div>

          {/* Content */}
          <div className="p-8 space-y-6">
            {/* Order Confirmation */}
            <div className="bg-gray-50 rounded-xl p-6">
              <h2 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
                <Package className="w-6 h-6 text-traqpack-yellow" />
                What Happens Next?
              </h2>
              
              <div className="space-y-4">
                <div className="flex items-start gap-4">
                  <div className="w-8 h-8 bg-traqpack-yellow rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                    <span className="text-sm font-bold text-black">1</span>
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900">Check Your Email</h3>
                    <p className="text-gray-600 text-sm mt-1">
                      We've sent your order confirmation and invoice to your email address.
                      {purchaseData?.device_imei && (
                        <span className="block mt-2">
                          Your device IMEI: <span className="font-mono font-bold">{purchaseData.device_imei}</span>
                        </span>
                      )}
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-8 h-8 bg-traqpack-yellow rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                    <span className="text-sm font-bold text-black">2</span>
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900">Device Shipping</h3>
                    <p className="text-gray-600 text-sm mt-1">
                      Your TraqPack device will be shipped within 1-2 business days. 
                      You'll receive tracking information once it's on the way.
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-8 h-8 bg-traqpack-yellow rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                    <span className="text-sm font-bold text-black">3</span>
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900">Activation</h3>
                    <p className="text-gray-600 text-sm mt-1">
                      Your device is pre-activated and ready to use! Simply power it on when you receive it, 
                      and it will start transmitting immediately.
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-8 h-8 bg-traqpack-yellow rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                    <span className="text-sm font-bold text-black">4</span>
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900">Start Tracking</h3>
                    <p className="text-gray-600 text-sm mt-1">
                      Log in to your dashboard to see real-time location, set up geofences, 
                      and configure alerts for your device.
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Subscription Info */}
            <div className="bg-blue-50 border border-blue-200 rounded-xl p-6">
              <h3 className="font-semibold text-blue-900 mb-2">
                Your Subscription Includes:
              </h3>
              <ul className="space-y-2 text-sm text-blue-800">
                <li className="flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-blue-600" />
                  <span>1 year of service (valued at $120)</span>
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-blue-600" />
                  <span>100 AI-powered queries per month</span>
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-blue-600" />
                  <span>50 smart notifications per month</span>
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-blue-600" />
                  <span>Unlimited location history for 365 days</span>
                </li>
              </ul>
              <p className="text-xs text-blue-700 mt-3">
                After the first year, continue service for just $10/year
              </p>
            </div>

            {/* Actions */}
            <div className="flex flex-col sm:flex-row gap-4">
              <button
                onClick={() => window.location.href = '/'}
                className="flex-1 bg-black text-traqpack-yellow font-bold py-3 px-6 rounded-xl hover:bg-gray-900 transition-all flex items-center justify-center gap-2"
              >
                Go to Dashboard
                <ArrowRight className="w-5 h-5" />
              </button>
              
              <a
                href="/"
                className="flex-1 bg-gray-100 text-gray-700 font-bold py-3 px-6 rounded-xl hover:bg-gray-200 transition-all flex items-center justify-center gap-2"
              >
                <Mail className="w-5 h-5" />
                View Purchase Details
              </a>
            </div>

            {/* Support */}
            <div className="text-center pt-4 border-t border-gray-200">
              <p className="text-sm text-gray-600">
                Need help? Contact us at{' '}
                <a href="mailto:support@traqpack.com" className="text-traqpack-yellow hover:underline font-semibold">
                  support@traqpack.com
                </a>
              </p>
            </div>
          </div>
        </div>

        {/* Additional Info */}
        <div className="mt-6 text-center">
          <p className="text-sm text-gray-600">
            Order confirmation has been sent to your email
          </p>
        </div>
      </div>
    </div>
  );
};

export default PurchaseSuccess;